import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X, Lightning } from "@phosphor-icons/react";

export default function PlanToProcurePage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    product_id: "", qty: "", project_code: "",
    plan_date: new Date().toISOString().slice(0, 10),
    procurement_category: "", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/alm/procurement-plans"), api.get("/alm/products")]);
    setRows(a.data); setProducts(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/procurement-plans", { ...form, qty: parseFloat(form.qty) });
    setOpen(false);
    setForm({ ...form, product_id: "", qty: "", project_code: "", procurement_category: "", notes: "" });
    load();
  };

  const generatePR = async (id) => { await api.post(`/alm/procurement-plans/${id}/generate-pr`); load(); };
  const remove = async (id) => { if (window.confirm("Delete this plan?")) { await api.delete(`/alm/procurement-plans/${id}`); load(); } };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Plan to Procure"
        subtitle="Plan demand for products, then convert plans into Purchase Requisitions."
        testid="alm-pp-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-pp-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> New Plan
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-pp-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Product", "Qty", "Project", "Category", "Plan Date", "Status", "PR #", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3"><span className="font-mono text-xs font-bold">{r.product_code}</span> <span className="text-xs text-gray-600">— {r.product_name}</span></td>
                  <td className="px-4 py-3 font-mono">{r.qty}</td>
                  <td className="px-4 py-3 text-xs">{r.project_code}</td>
                  <td className="px-4 py-3 text-xs">{r.procurement_category}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.plan_date)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold ${r.status === "pr_generated" ? "text-green-700" : "text-gray-900"}`}>
                      {(r.status || "planned").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.pr_number || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status !== "pr_generated" && (
                      <button onClick={() => generatePR(r.id)} data-testid={`alm-pp-genpr-${r.id}`} className="text-[#0020C2] hover:underline text-xs font-bold inline-flex items-center gap-1 mr-3">
                        <Lightning size={12} weight="bold" /> Generate PR
                      </button>
                    )}
                    <button onClick={() => remove(r.id)} data-testid={`alm-pp-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">No procurement plans yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-pp-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Procurement Plan</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Product *</label>
                <select required value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} data-testid="alm-pp-product" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Product —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                </select>
              </div>
              {[
                ["qty", "Quantity *", "number", true],
                ["project_code", "Project Code", "text", false],
                ["procurement_category", "Procurement Category", "text", false],
                ["plan_date", "Plan Date *", "date", true],
                ["notes", "Notes", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-pp-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-pp-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create Plan</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
